import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface IndustryVoice {
  id: string;
  quote: string;
  name: string;
  role: string;
  source: string | null;
  avatar: string | null;
  featured: boolean;
  display_order: number;
}

const fallbackVoices: IndustryVoice[] = [
  {
    id: "1", quote: "The customer does not want a drilling machine; he wants a hole in the wall. Outcome-based contracting reflects this reality by aligning payment with the achieved result, not the ownership of the asset.",
    name: "Theodore Levitt", role: "Economist & Harvard Business School Professor", source: "https://en.wikipedia.org/wiki/Theodore_Levitt",
    avatar: "https://bepnmmikbflhezryppcs.supabase.co/storage/v1/object/public/pictures1/voice-cards/harvard.png", featured: true, display_order: 1,
  },
  {
    id: "2", quote: "Performance-based contracting is reshaping after-sales service supply chains in capital-intensive industries by linking supplier compensation to product availability and customer-generated output, thereby improving uptime while reducing total cost of ownership.",
    name: "Sang-Hyun Kim, Morris A. Cohen & Serguei Netessine", role: "Wharton School, University of Pennsylvania", source: "https://faculty.wharton.upenn.edu/wp-content/uploads/2012/04/Performance-Contracting-in-After-Sales-Service-Supply-Chains.pdf",
    avatar: "https://bepnmmikbflhezryppcs.supabase.co/storage/v1/object/public/pictures1/voice-cards/Wharton%20School%20University%20of%20Pennsylvania.png", featured: true, display_order: 2,
  },
  {
    id: "3", quote: "Outcome-based contracting with multiple competitors is vastly better than cost-plus (especially if sole-sourced), as the former rewards results & latter rewards waste. Outcome contracting should be applied broadly within government. The difference in results will be incredible.",
    name: "Elon Musk", role: "CEO, SpaceX & Tesla", source: "https://x.com/elonmusk/status/1270059745538265088",
    avatar: "https://bepnmmikbflhezryppcs.supabase.co/storage/v1/object/public/pictures1/voice-cards/Elon_Musk", featured: false, display_order: 3,
  },
  {
    id: "4", quote: "Service science, as an integrative discipline of engineering, technology, and social sciences, finds a natural experimental setting in outcome-based contracts, where value co-creation can be analyzed within complex service systems composed of people, technologies, and institutional structures.",
    name: "Irene C. L. Ng et al., Steve Lohr & Jim Spohrer et al.", role: "University of Exeter", source: "https://www.sciencedirect.com/science/article/abs/pii/S0263237309000541",
    avatar: "https://bepnmmikbflhezryppcs.supabase.co/storage/v1/object/public/pictures1/voice-cards/exeter.png", featured: true, display_order: 4,
  },
  {
    id: "5", quote: "Outcome-based contracts represent the most advanced stage of service offerings, requiring firms to shift from product-centric models to outcome business models that fundamentally transform value creation. Companies like Caterpillar are moving beyond selling machines to guaranteeing outcomes such as uptime, efficiency, and performance—redefining value in capital-intensive industries.",
    name: "Caterpillar (CAT)", role: "Power Generation & Mining Sectors", source: "https://www.caterpillar.com/en/news/corporate-press-releases/h/joule-caterpillar-wheeler.html",
    avatar: "https://bepnmmikbflhezryppcs.supabase.co/storage/v1/object/public/pictures1/voice-cards/CAT", featured: false, display_order: 5,
  },
  {
    id: "6", quote: "If you're competitor focused, you have to wait until there is a competitor doing something. Being customer-focused allows you to be more pioneering.",
    name: "Jeff Bezos", role: "Founder & Executive Chairman, Amazon", source: null,
    avatar: "https://bepnmmikbflhezryppcs.supabase.co/storage/v1/object/public/pictures1/voice-cards/jeff.webp", featured: false, display_order: 6,
  },
];

export const useIndustryVoices = () => {
  return useQuery({
    queryKey: ["industry-voices"],
    queryFn: async (): Promise<IndustryVoice[]> => {
      const { data, error } = await supabase
        .from("industry_voices")
        .select("*")
        .order("display_order", { ascending: true });

      // Fall back to the curated list when the table is empty or unreachable
      if (error || !data || data.length === 0) return fallbackVoices;
      return data as IndustryVoice[];
    },
    staleTime: 1000 * 60 * 30,
    placeholderData: fallbackVoices,
  });
};
